import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { User } from '@supabase/supabase-js';
import type { QiRole } from './types';

export interface QiRoleState {
  role: QiRole;
  loading: boolean;
}

// access role（qi_roles.role）只認 admin / reviewer 兩種「管理／覆核角色」。
// 「施炁者」資格完全由 qi_practitioners 判定（approved + formally_authorized），
// 與 access role 解耦 —— 所以 datai 的 role=member 但資格 approved 時，
// 前端仍判定為 practitioner，與後端 is_qi_practitioner() 一致。
export function useQiRole(user: User | null): QiRoleState {
  const [state, setState] = useState<QiRoleState>({ role: 'member', loading: false });

  useEffect(() => {
    // 以 user.id 為依賴，而非整個 user 物件：token 自動刷新會產生新的
    // user 物件（同一人），若用物件參考值會重複觸發 loading → 導致上層
    // AuthGate 卸載表單、清空已填資料。
    const userId = user?.id ?? null;
    if (!userId) {
      setState({ role: 'member', loading: false });
      return;
    }

    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true }));

    (async () => {
      try {
        const { data } = await supabase
          .from('qi_roles')
          .select('role')
          .eq('user_id', userId)
          .maybeSingle();

        let role: QiRole =
          data && (data.role === 'admin' || data.role === 'reviewer')
            ? (data.role as QiRole)
            : 'member';

        // 只有非管理／覆核角色才檢查施炁者資格
        if (role === 'member') {
          const { data: profile, error: profileErr } = await supabase.rpc(
            'get_my_practitioner_profile',
          );
          if (!profileErr && profile?.exists === true) {
            const status = profile.practitioner_status as string | null;
            // 正式授權（新真值 practitioner_status='authorized'，或舊欄位兜底）
            const formallyAuthorized =
              status === 'authorized' ||
              (profile.qualification_status === 'approved' &&
                profile.practice_authorization_status === 'formally_authorized');
            // 準施炁者（培育中）
            const isCandidate = status === 'candidate' || status === 'mentor_review';

            if (formallyAuthorized) role = 'practitioner';
            else if (isCandidate) role = 'candidate';
          }
        }

        if (!cancelled) setState({ role, loading: false });
      } catch {
        if (!cancelled) setState({ role: 'member', loading: false });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  return state;
}